import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAward } from '@fortawesome/free-solid-svg-icons';
import FormResultMessage from './FormResultMessage';
import {
  fetchHVACampaigns,
  HVACampaign,
  fetchHVACampaignScorecards,
  assignCampaignScorecards
} from 'services/hvaservicecampaign.service';
import {
  fetchHVAScorecards,
  HVAScorecard
} from 'services/hvascorecard.service';

const ScorecardAssignmentForm: React.FC = () => {
  const [campaigns, setCampaigns] = useState<HVACampaign[]>([]);
  const [scorecards, setScorecards] = useState<HVAScorecard[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<number | null>(null);
  // eslint-disable-next-line
  const [selectedScorecards, setSelectedScorecards] = useState<Set<number>>(new Set());

  const [message, setMessage] = useState<string | null>(null);
  // eslint-disable-next-line
  const [messageType, setMessageType] = useState<'successupdate' | 'errorupdate'>('successupdate');
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  // Carga campañas y scorecards
  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const campaignsData = await fetchHVACampaigns();
        setCampaigns(campaignsData);
      } catch (error) {
        console.error('Error loading campaigns:', error);
      }
    };

    const loadScorecards = async () => {
      try {
        const scorecardsData = await fetchHVAScorecards();
        setScorecards(scorecardsData);
      } catch (error) {
        console.error('Error loading scorecards:', error);
      }
    };

    loadCampaigns();
    loadScorecards();
  }, []);

  // Carga las scorecards de una campaña
  useEffect(() => {
    const loadCampaignScorecards = async () => {
      if (selectedCampaign !== null) {
        try {
          // eslint-disable-next-line
          const campaignScorecardIds = await fetchHVACampaignScorecards(selectedCampaign);
          setSelectedScorecards(new Set(campaignScorecardIds));
        } catch (error) {
          console.error('Error loading campaign scorecards:', error);
          setSelectedScorecards(new Set());
        }
      } else {
        setSelectedScorecards(new Set());
      }
    };

    loadCampaignScorecards();
  }, [selectedCampaign]);

  const handleCampaignSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedCampaign(Number(event.target.value));
  };

  const handleScorecardSelect = (scorecardId: number) => {
    setSelectedScorecards(prevSelectedScorecards => {
      const newSelectedScorecards = new Set(prevSelectedScorecards);
      if (newSelectedScorecards.has(scorecardId)) {
        newSelectedScorecards.delete(scorecardId);
      } else {
        newSelectedScorecards.add(scorecardId);
      }
      return newSelectedScorecards;
    });
  };

  const handleAssign = async () => {
    if (selectedCampaign !== null) {
      try {
        await assignCampaignScorecards(
          selectedCampaign,
          Array.from(selectedScorecards)
        );
        if (selectedScorecards.size > 0) {
          setMessage(
            'Well done! The selected scorecards have been assigned to the campaign.'
          );
        } else {
          setMessage('All scorecards have been removed from the campaign.');
        }
        setMessageType('successupdate');
      } catch (error) {
        setMessage(
          'An error occurred while updating scorecard assignments: ' + error
        );
        setMessageType('errorupdate');
      }
      setModalVisible(true);
    } else {
      alert('Please select a campaign.');
    }
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  return (
    <Row className="p-0">
      <Col className="">
        <h3 className="px-2 text-secondary-darker">Campaigns</h3>
        <Card className="bg-body">
          <Card.Body>
            <Form.Group>
              <div
                className="scrollbar px-2"
                style={{ height: '350px', overflowY: 'scroll' }}
              >
                {campaigns.map(campaign => (
                  <Form.Check
                    key={campaign.id}
                    type="radio"
                    name="risk-campaigns"
                    id={`risk-campaign-${campaign.id}`}
                    label={`(${campaign.servicename}) - ${campaign.name}`}
                    value={campaign.id}
                    onChange={handleCampaignSelect}
                    checked={selectedCampaign === campaign.id}
                  />
                ))}
              </div>
            </Form.Group>
          </Card.Body>
        </Card>
      </Col>
      <Col className="scorecards-column">
        <h3 className="px-2 text-secondary-darker">Scorecards</h3>
        <Card className="bg-body scorecardassignment-card">
          <Card.Body>
            <Form.Group>
              <div
                className="scrollbar px-2"
                style={{ height: '350px', overflowY: 'scroll' }}
              >
                {scorecards.map(scorecard => (
                  <Form.Check
                    id={`scorecard-${scorecard.id}`}
                    key={scorecard.id}
                    type="checkbox"
                    label={scorecard.name}
                    onChange={() => handleScorecardSelect(scorecard.id)}
                    checked={selectedScorecards.has(scorecard.id)}
                  />
                ))}
              </div>
            </Form.Group>
          </Card.Body>
        </Card>
        <div className="primary pt-3">
          <Button
            className="btn btn-primary btn-block px-4"
            onClick={handleAssign}
          >
            <FontAwesomeIcon icon={faAward} className="me-2 fs-8" />
            Assign selected scorecards to campaign
          </Button>
        </div>
      </Col>
      <FormResultMessage
        show={modalVisible}
        message={message}
        messageType={messageType}
        logout={false}
        onClose={closeModal}
      />
    </Row>
  );
};

export default ScorecardAssignmentForm;
